import { type FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  marketingErrorKey,
  useContactListQuery,
} from "@/partner/entities/marketing";
import { DialogHeader } from "@/shared/components";
import { ContactListNameDialog } from "./ContactListNameDialog";
import { ContactsTable } from "./ContactsTable";
import { ImportContactsDialog } from "./ImportContactsDialog";

interface ContactListDialogProps {
  id: string;
  onClose: () => void;
}

/**
 * Диалог списка контактов (?tab=edit&id=): fullScreen с DialogHeader, как
 * диалог рассылки. В шапке переименование и импорт, в теле таблица
 * контактов. Переименование и импорт открываются поверх, адрес не меняют.
 */
export const ContactListDialog: FC<ContactListDialogProps> = ({
  id,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [renaming, setRenaming] = useState(false);
  const [importing, setImporting] = useState(false);
  const { data, error } = useContactListQuery({
    variables: { id },
    fetchPolicy: "cache-and-network",
  });
  const list = data?.contactList;

  // Список не загрузился (ошибка или его уже удалили): снекбар и закрыть
  // диалог, иначе на странице остаётся ?tab=edit без содержимого.
  const loadFailed = Boolean(error) && !list;
  const failureHandled = useRef(false);
  useEffect(() => {
    if (!loadFailed || failureHandled.current) return;
    failureHandled.current = true;
    enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" });
    onClose();
  }, [loadFailed, error, enqueueSnackbar, t, onClose]);

  const openImport = () => setImporting(true);

  return (
    <Dialog open onClose={onClose} fullScreen>
      <DialogHeader
        title={list?.name ?? ""}
        loading={!list}
        onClose={onClose}
        actions={
          list && [
            <Button
              key="rename"
              variant="outlined"
              onClick={() => setRenaming(true)}
            >
              {t("marketingContacts.edit.rename")}
            </Button>,
            <Button
              key="import"
              variant="contained"
              disableElevation
              onClick={openImport}
            >
              {t("marketingContacts.import.open")}
            </Button>,
          ]
        }
      />
      <DialogContent sx={{ p: 1 }}>
        {list ? (
          <ContactsTable listId={list.id} onImport={openImport} />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      {list && renaming && (
        <ContactListNameDialog list={list} onClose={() => setRenaming(false)} />
      )}
      {list && importing && (
        <ImportContactsDialog
          listId={list.id}
          onClose={() => setImporting(false)}
        />
      )}
    </Dialog>
  );
};
